import {
	autoUpdate,
	computePosition,
	flip,
	offset,
	shift,
} from "@floating-ui/dom";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";
import type { MessageSync, Stream } from "sdk";
import {
	absolutePositionToRelativePosition,
	relativePositionToAbsolutePosition,
	ySyncPluginKey,
} from "y-prosemirror";
import * as Y from "yjs";
import type { Api } from "@/api";
import { getColor } from "@/lib/colors";
import { base64UrlDecode, base64UrlEncode } from "./editor-utils.ts";

export const cursorPluginKey = new PluginKey("cursorPlugin");

export const handleDocumentPresence = (
	view: EditorView,
	msg: MessageSync,
	channelId: string,
	branchId: string,
	api: Api,
) => {
	if (
		msg.type === "DocumentPresence" &&
		msg.channel_id === channelId &&
		msg.branch_id === branchId
	) {
		const currentUser = api.users.cache.get("@self");
		const selfId = currentUser?.id;
		if (msg.user_id === selfId) return;

		let cursor = null;
		if (msg.cursor_head) {
			try {
				const head = Y.decodeRelativePosition(base64UrlDecode(msg.cursor_head));
				const anchor = msg.cursor_tail
					? Y.decodeRelativePosition(base64UrlDecode(msg.cursor_tail))
					: head;
				cursor = {
					head: Y.relativePositionToJSON(head),
					anchor: Y.relativePositionToJSON(anchor),
				};
			} catch (e) {
				console.error("failed to decode cursor", e);
			}
		}

		// FIXME: use room_member.override_name if it exists
		// FIXME: update name live
		const user = api.users.cache.get(msg.user_id);
		const name = user?.name || "Unknown";
		const color = getColor(msg.user_id);

		const tr = view.state.tr;
		tr.setMeta(cursorPluginKey, {
			type: cursor ? "update" : "remove",
			userId: msg.user_id,
			name,
			color,
			cursor,
		});
		view.dispatch(tr);
	}
};

export const cursorPlugin = (
	api: Api,
	channelId: string,
	branchId: string,
	isSubscribed: () => boolean,
	showCursors?: () => boolean,
	getStream?: () => Stream | null,
) => {
	return new Plugin({
		key: cursorPluginKey,
		state: {
			init() {
				return { cursors: new Map(), isSubscribed };
			},
			apply(tr, value) {
				const meta = tr.getMeta(cursorPluginKey);
				if (meta) {
					const newCursors = new Map(value.cursors);
					if (meta.type === "update") {
						newCursors.set(meta.userId, {
							name: meta.name,
							color: meta.color,
							cursor: meta.cursor,
						});
					} else if (meta.type === "remove") {
						newCursors.delete(meta.userId);
					}
					return { cursors: newCursors, isSubscribed: value.isSubscribed };
				}
				return value;
			},
		},
		props: {
			decorations(state) {
				// Hide cursors when showCursors returns false (e.g., during diff/history view)
				if (showCursors?.() === false) {
					return DecorationSet.empty;
				}

				const { cursors } = cursorPluginKey.getState(state);
				const decos: Decoration[] = [];
				const yState = ySyncPluginKey.getState(state);

				if (!yState || !yState.binding) return DecorationSet.empty;

				const currentUser = api.users.cache.get("@self");
				const selfId = currentUser?.id;

				for (const [userId, data] of cursors) {
					if (userId === selfId) continue;

					const anchor = relativePositionToAbsolutePosition(
						yState.doc,
						yState.type,
						Y.createRelativePositionFromJSON(data.cursor.anchor),
						yState.binding.mapping,
					);
					const head = relativePositionToAbsolutePosition(
						yState.doc,
						yState.type,
						Y.createRelativePositionFromJSON(data.cursor.head),
						yState.binding.mapping,
					);

					if (anchor === null || head === null) continue;

					// render selections
					const from = Math.min(anchor, head);
					const to = Math.max(anchor, head);

					if (from !== to) {
						decos.push(
							Decoration.inline(from, to, {
								style: `background-color: color-mix(in srgb, ${data.color}, transparent 70%)`,
							}),
						);
					}

					// render cursors
					decos.push(
						Decoration.widget(
							head,
							(_view) => {
								const widget = document.createElement("span");
								widget.classList.add("document-presence-cursor");
								widget.style.borderLeft = `2px solid ${data.color}`;

								const label = document.createElement("div");
								label.classList.add("document-presence-name");
								label.textContent = data.name;
								label.style.backgroundColor = data.color;

								widget.appendChild(label);

								const cleanup = autoUpdate(widget, label, () => {
									computePosition(widget, label, {
										placement: "top-start",
										middleware: [offset(4), flip(), shift({ padding: 4 })],
									}).then(({ x, y }) => {
										label.style.translate = `${x}px ${y}px`;
									});
								});

								// Store cleanup function on widget DOM element
								(
									widget as HTMLElement & { _floating_cleanup?: () => void }
								)._floating_cleanup = cleanup;

								return widget;
							},
							{
								key: userId,
								destroy(dom) {
									const domEl = dom as HTMLElement & {
										_floating_cleanup?: () => void;
									};
									if (domEl._floating_cleanup) {
										domEl._floating_cleanup();
									}
								},
							},
						),
					);
				}

				return DecorationSet.create(state.doc, decos);
			},
		},
		view(view) {
			const onSync = (payload: [MessageSync, unknown]) => {
				const [msg] = payload;
				handleDocumentPresence(view, msg, channelId, branchId, api);
			};

			const unsubscribe = api.events.on("sync", onSync);

			return {
				update(view, prevState) {
					const yState = ySyncPluginKey.getState(view.state);
					if (!yState || !yState.binding) return;

					const { isSubscribed } = cursorPluginKey.getState(view.state);
					if (!isSubscribed()) return;

					const sel = view.state.selection;
					const prevSel = prevState.selection;

					if (!sel.eq(prevSel)) {
						const { anchor, head } = sel;
						const anchorRel = absolutePositionToRelativePosition(
							anchor,
							yState.type,
							yState.binding.mapping,
						);
						const headRel = absolutePositionToRelativePosition(
							head,
							yState.type,
							yState.binding.mapping,
						);

						const anchorEnc = base64UrlEncode(
							Y.encodeRelativePosition(anchorRel),
						);
						const headEnc = base64UrlEncode(Y.encodeRelativePosition(headRel));

						const data = {
							type: "DocumentPresence" as const,
							channel_id: channelId,
							branch_id: branchId,
							cursor_head: headEnc,
							cursor_tail: anchorEnc,
						};

						const stream = getStream?.();
						if (stream) {
							stream.send(data);
						} else {
							api.client.send(data);
						}
					}
				},
				destroy() {
					unsubscribe();
				},
			};
		},
	});
};
